/**
 * @fileoverview Publish Modal Component - Production Implementation
 * @description Modal for bulk publishing assignments with real API integration
 * @author MCHD Room Scheduler Team
 * @created 2025-01-08
 * @updated 08-10-2025 10:41:19 - Removed ALL mock data, connected to real APIs
 * @complies @react-frontend.mdc, @ui-ux-excellence.mdc, @tech-stack-documentation.mdc
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  Button,
  Group,
  Alert,
  Loader,
  Box,
  Badge,
  Divider,
  ScrollArea,
  TextInput,
  Grid,
  Card,
  Checkbox,
  RingProgress,
  Center,
  Accordion
} from '@mantine/core';
import {
  IconUpload,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconAlertTriangle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useBulkPublish } from '../../hooks/api/usePublish';
import { useBuildingsForSelection } from '../../hooks/admin/useBuildings';
import { getFloorsForBuilding } from '../../api/endpoints/rooms';
import { useCoverageStats } from '../../hooks/api/usePublish';

/**
 * PublishModal Props Interface
 */
interface PublishModalProps {
  opened: boolean;
  onClose: () => void;
  publishData?: {
    date?: string;
    scope?: string;
    buildingId?: string;
    floorId?: string;
    roomIds?: string[];
  };
  loading?: boolean;
}

/**
 * PublishModal Component
 * 
 * Features from UX Touchpoint Spec Section 3.6:
 * - Select publish date and scope (day/week/month)
 * - Optional building/floor filtering
 * - Real-time coverage statistics
 * - Room-by-room breakdown
 * - Conflict count and handling options
 * - Confirmation before publish
 * - Audit logging on publish
 * 
 * NO MOCK DATA - All data from real backend APIs
 * 
 * @created 2025-01-08
 * @updated 08-10-2025 10:41:19 - Production implementation (Gap PUBLISH-1 resolved)
 */
const PublishModal: React.FC<PublishModalProps> = ({
  opened,
  onClose,
  publishData,
  loading = false
}) => {
  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================
  
  const [date, setDate] = useState<string>(publishData?.date || '');
  const [scope, setScope] = useState<'day' | 'week' | 'month'>('day');
  const [buildingId, setBuildingId] = useState<string>(publishData?.buildingId || '');
  const [floorId, setFloorId] = useState<string>(publishData?.floorId || '');
  const [roomIds, setRoomIds] = useState<string[]>(publishData?.roomIds || []);
  const [options, setOptions] = useState({
    forcePublish: false,
    skipConflicts: false
  });
  const [submitting, setSubmitting] = useState(false);

  // =============================================================================
  // API HOOKS - Real Data Fetching
  // =============================================================================
  
  const bulkPublishMutation = useBulkPublish();
  
  // Fetch real buildings from API
  const { data: buildingsResponse, isLoading: buildingsLoading } = useBuildingsForSelection();
  const buildings = buildingsResponse?.data?.buildings || [];
  
  // Fetch floors based on selected building
  const [floors, setFloors] = useState<any[]>([]);
  const [floorsLoading, setFloorsLoading] = useState(false);
  
  
  // Fetch coverage stats when date is selected
  const { 
    data: coverageData, 
    isLoading: coverageLoading,
    error: coverageError
  } = useCoverageStats({
    startDate: date,
    endDate: date, // For now, same date - scope handled by backend
    ...(buildingId && { buildingId }),
    ...(floorId && { floorId })
  }, opened && !!date);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setDate(publishData?.date || new Date().toISOString().split('T')[0]);
      const scopeValue = publishData?.scope;
      if (scopeValue === 'day' || scopeValue === 'week' || scopeValue === 'month') {
        setScope(scopeValue);
      } else {
        setScope('day');
      }
      setBuildingId(publishData?.buildingId || '');
      setFloorId(publishData?.floorId || '');
      setRoomIds(publishData?.roomIds || []);
      setOptions({ forcePublish: false, skipConflicts: false });
    }
  }, [opened, publishData]);

  // Fetch floors when building is selected
  useEffect(() => {
    if (buildingId && opened) {
      fetchFloors(buildingId);
    } else {
      setFloors([]);
      setFloorId('');
    }
  }, [buildingId, opened]);

  /**
   * Fetch floors for selected building from API
   * @param selectedBuildingId - Building ID
   * 
   * @created 08-10-2025 10:41:19
   */
  const fetchFloors = async (selectedBuildingId: string) => {
    setFloorsLoading(true);
    try {
      const floorsData = await getFloorsForBuilding(selectedBuildingId);
      setFloors(floorsData || []);
    } catch (error) {
      console.error('Error fetching floors:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load floors for building',
        color: 'red'
      });
      setFloors([]);
    } finally {
      setFloorsLoading(false);
    }
  };

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  /**
   * Handle publish operation
   * Validates inputs, calls API, handles response
   * 
   * @created 08-10-2025 10:41:19
   */
  const handlePublish = async () => {
    if (!date) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a date',
        color: 'red'
      });
      return;
    }

    // Check for conflicts if not using force/skip options
    if (!options.forcePublish && !options.skipConflicts && coverageData && coverageData.conflicts > 0) {
      notifications.show({
        title: 'Conflicts Detected',
        message: `${coverageData.conflicts} assignment(s) have conflicts. Enable "Force publish" or "Skip conflicts" to proceed.`,
        color: 'orange',
        icon: <IconAlertTriangle size={16} />
      });
      return;
    }

    setSubmitting(true);
    try {
      // Prepare publish data for API
      const publishParams = {
        date,
        scope,
        ...(buildingId && { buildingId }),
        ...(floorId && { floorId }),
        ...(roomIds.length > 0 && { roomIds }),
        options: {
          forcePublish: options.forcePublish,
          skipConflicts: options.skipConflicts
        }
      };

      // Call real API
      const result = await bulkPublishMutation.mutateAsync(publishParams);
      
      notifications.show({
        title: 'Success',
        message: `Successfully published ${result.data?.publishedCount || 0} assignment(s)`,
        color: 'green',
        icon: <IconCheck size={16} />
      });
      
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Publish Failed',
        message: error instanceof Error ? error.message : 'Failed to publish. Fix conflicts first.',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const canPublish = date && !submitting && !loading && !bulkPublishMutation.isPending;
  const isLoadingData = buildingsLoading || floorsLoading || coverageLoading;

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconUpload size={20} />
          <Text fw={600}>Publish Schedule</Text>
        </Group>
      }
      size={700}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {/* Publish Parameters */}
        <Grid>
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">Publish Date *</Text>
              <TextInput
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading || submitting}
                required
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">Scope</Text>
              <Select
                placeholder="Select scope"
                data={[
                  { value: 'day', label: 'Single Day' },
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' }
                ]}
                value={scope}
                onChange={(value) => {
                  if (value === 'day' || value === 'week' || value === 'month') {
                    setScope(value);
                  }
                }}
                disabled={loading || submitting}
              />
            </Box>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">Building (Optional)</Text>
              <Select
                placeholder="Select building"
                data={buildings.map(building => ({
                  value: building.id,
                  label: building.name
                }))}
                value={buildingId}
                onChange={(value) => setBuildingId(value || '')}
                disabled={loading || submitting || buildingsLoading}
                clearable
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">Floor (Optional)</Text>
              <Select
                placeholder="Select floor"
                data={floors.map(floor => ({
                  value: floor.id,
                  label: floor.name
                }))}
                value={floorId}
                onChange={(value) => setFloorId(value || '')}
                disabled={loading || submitting || !buildingId || floorsLoading}
                rightSection={floorsLoading ? <Loader size="xs" /> : null}
                clearable
              />
            </Box>
          </Grid.Col>
        </Grid>

        {/* Coverage Statistics - Real Data from API */}
        {coverageLoading && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Loading coverage statistics...</Text>
            </Stack>
          </Center>
        )}

        {coverageError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">Failed to load coverage statistics. Please try again.</Text>
          </Alert>
        )}

        {coverageData && !coverageLoading && (
          <Box>
            <Divider my="md" />
            <Text fw={600} mb="md">Coverage Statistics</Text>
            
            <Grid>
              <Grid.Col span={3}>
                <Card withBorder p="sm">
                  <Stack align="center" gap="xs">
                    <RingProgress
                      size={60}
                      thickness={6}
                      sections={[{ value: coverageData.coverage, color: 'blue' }]}
                      label={
                        <Text size="xs" ta="center" fw={600}>
                          {coverageData.coverage}%
                        </Text>
                      }
                    />
                    <Text fw={500} size="sm">Coverage</Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={3}>
                <Card withBorder p="sm">
                  <Stack align="center" gap="xs">
                    <Text size="xl" fw={700} c="green">{coverageData.published}</Text>
                    <Text fw={500} size="sm">Published</Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={3}>
                <Card withBorder p="sm">
                  <Stack align="center" gap="xs">
                    <Text size="xl" fw={700} c="orange">{coverageData.draft}</Text>
                    <Text fw={500} size="sm">Draft</Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={3}>
                <Card withBorder p="sm">
                  <Stack align="center" gap="xs">
                    <Text size="xl" fw={700} c={coverageData.conflicts > 0 ? 'red' : 'green'}>
                      {coverageData.conflicts}
                    </Text>
                    <Text fw={500} size="sm">Conflicts</Text>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Room Breakdown - Real Data */}
            {coverageData.byRoom && coverageData.byRoom.length > 0 && (
              <Box mt="md">
                <Text fw={500} mb="xs">Room Breakdown</Text>
                <ScrollArea h={200}>
                  <Stack gap="xs">
                    {coverageData.byRoom.map((room) => (
                      <Card key={room.roomId} withBorder p="sm">
                        <Group justify="space-between">
                          <Group gap="md">
                            <Text fw={500}>{room.roomNumber}</Text>
                            <Text size="sm" c="dimmed">
                              {room.published} published, {room.draft} draft
                            </Text>
                          </Group>
                          <Group gap="xs">
                            <Badge color="green" variant="light">
                              {room.published}
                            </Badge>
                            <Badge color="orange" variant="light">
                              {room.draft}
                            </Badge>
                            <Badge color="blue" variant="light">
                              {room.coverage}%
                            </Badge>
                          </Group>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                </ScrollArea>
              </Box>
            )}
            
            {/* Inline Conflict Details */}
            {coverageData && coverageData.conflicts > 0 && (
              <Box mt="md">
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  title="Conflicts Detected"
                  color="red"
                  variant="light"
                >
                  <Stack gap="sm">
                    <Text size="sm">
                      {coverageData.conflicts} assignment{coverageData.conflicts > 1 ? 's' : ''} have conflicts. 
                      Review and resolve before publishing, or use publish options below.
                    </Text>
                    <Accordion variant="separated">
                      <Accordion.Item value="conflict-details">
                        <Accordion.Control>
                          <Group gap="xs">
                            <IconAlertTriangle size={14} />
                            <Text size="sm" fw={500}>View Conflict Details</Text>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Text size="xs" c="dimmed">
                            Common conflicts include room capacity exceeded, doctor double booking, 
                            blocked time slots, and specialty mismatches. Resolve conflicts before publishing.
                          </Text>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  </Stack>
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {/* Options */}
        <Box>
          <Text size="sm" fw={500} mb="xs">Publish Options</Text>
          <Stack gap="xs">
            <Checkbox
              label="Force publish (ignore conflicts)"
              checked={options.forcePublish}
              onChange={(e) => setOptions({ ...options, forcePublish: e.currentTarget.checked })}
              disabled={loading || submitting}
            />
            <Checkbox
              label="Skip assignments with conflicts"
              checked={options.skipConflicts}
              onChange={(e) => setOptions({ ...options, skipConflicts: e.currentTarget.checked })}
              disabled={loading || submitting}
            />
          </Stack>
        </Box>

        {/* Confirmation - UX Spec Section 3.6 */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Confirm Publish"
          color="blue"
          variant="light"
        >
          <Text size="sm">
            Publish schedule for selected date range? Doctors will see published assignments.
          </Text>
        </Alert>

        {/* Loading State */}
        {isLoadingData && (
          <Box ta="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed" mt="xs">
              Loading data...
            </Text>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="light"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!canPublish}
            loading={submitting || bulkPublishMutation.isPending}
            leftSection={<IconUpload size={16} />}
            color="green"
          >
            {(submitting || bulkPublishMutation.isPending) ? 'Publishing...' : 'Publish Schedule'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default PublishModal;
